import React from 'react';
import { oneOf, oneOfType, node, func, string, bool, object } from 'prop-types';
import classnames from 'classnames';
import Icon from '../Icon';
import {
    BASE_CLASSES,
    BASE_ICON_CLASS,
    ICON_POSITIONS
} from './constants';
import './index.scss';

/**
 * Generates the input's icon, supporting both icon props and an <Icon> element.
 * @param {React.Element} [options.icon] Either an <svg> or an <Icon> element.
 * @param {String} [options.iconPosition] The icon's position.
 * @param {Function} [options.onIconClick] The callback function to execute on icon clicks.
 * @return {React.Element}
 */
const generateIcon = ({ icon, iconPosition, onIconClick } = {}) => {
    if (!icon) { return null; }

    if (!React.isValidElement(icon)) {
        const props = {
            className: BASE_ICON_CLASS,
            iconPosition,
            size: 14,
            children: icon
        };

        if (typeof onIconClick === 'function') {
            props.onClick = onIconClick;
        }

        return <Icon {...props}/>;
    }

    const isIconComponent = icon.type === Icon;

    if (!isIconComponent) { return null; }

    return React.cloneElement(icon, {
        className: classnames(icon.className, BASE_ICON_CLASS)
    });
};

const Input = ({
    type,
    className,
    disabled,
    icon,
    iconPosition,
    onIconClick,
    innerRef,
    ...rest
}) => {
    const classes = classnames('fit-input-wrapper', className, {
        'fit-input-disabled': disabled,
        'fit-input-has-icon': icon,
        'fit-input-icon-start': iconPosition === 'start'
    });

    const inputProps = {
        disabled,
        type,
        ref: innerRef,
        ...rest
    };

    return (
        <div className={classes}>
            <div className="fit-input-inner-wrapper">
                <input className={BASE_CLASSES}
                    {...inputProps}/>
                {generateIcon({ icon, iconPosition, onIconClick })}
            </div>
        </div>
    );
};

Input.propTypes = {
    /**
     * Basically every native input type that renders a "boxish" input.
     */
    type: oneOf([
        'text',
        'password',
        'number',
        'tel'
    ]),
    /**
     * Whether or not the input is disabled.
     * A disabled input is not clickable, or hoverable.
     */
    disabled: bool,
    /**
     * Either an `<svg>` element or an `<Icon>` component.
     */
    icon: node,
    /**
     * Where to display the icon. Relevant only when `icon` is an `<svg>`.
     * Supported options are exposed through the static `ICON_POSITIONS` property.
     */
    iconPosition: oneOf(Object.values(ICON_POSITIONS)),
    /**
     * Callback function to execute when the icon is clicked.
     * Relevant only when `icon` is an `<svg>`.
     */
    onIconClick: func,
    /**
     * Provides a React `ref` access to the component's `<input>` element.
     */
    innerRef: oneOfType([func, object]),
    /**
     * Custom classes you wish to pass to the component.
     */
    className: string
};

Input.defaultProps = {
    type: 'text',
    disabled: false,
    iconPosition: ICON_POSITIONS.START
};

Object.assign(Input, {
    ICON_POSITIONS
});

export default Input;
